import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useEventTracking } from '../../hooks/useEventTracking';
import { useAuthStore } from '../../store/authStore';
import { useForegroundTiming } from '../../hooks/useForegroundTiming';
import { recordClientActivity } from '../../services/activityApi';
import glassTheme, { glassMixins } from '../../styles/glassTheme';

const PageContainer = styled.div`
  min-height: 100vh;
  ${glassTheme.gridBackground}
`;

const Header = styled.div<{ $color: string }>`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 2rem;
  box-shadow: ${glassTheme.shadow.lg};
  border-top: 4px solid ${props => props.$color};
`;

const HeaderContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: ${glassTheme.colors.text.muted};
  margin-bottom: 1rem;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  &:hover {
    color: ${glassTheme.colors.accent.blue};
    text-decoration: underline;
  }
`;

const CategoryBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 0.35rem 1rem;
  background: ${props => props.$color}33;
  color: ${props => props.$color};
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 0.9rem;
  color: ${glassTheme.colors.text.muted};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${glassTheme.colors.accent.green} 0%, ${glassTheme.colors.accent.cyan} 100%);
  border-radius: 3px;
  transition: width 0.5s ease;
  width: ${props => props.$progress}%;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  margin-top: -1.5rem;
  margin-bottom: 1rem;
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.div`
  ${glassTheme.glass.container}
  padding: 2rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${glassTheme.colors.borderHover};
    box-shadow: ${glassTheme.shadow.lg};
  }
`;

const CardHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const CardNumber = styled.span<{ $index: number }>`
  display: inline-block;
  width: 32px;
  height: 32px;
  background: ${glassTheme.gradients.primary};
  color: ${glassTheme.colors.text.primary};
  border-radius: 50%;
  text-align: center;
  line-height: 32px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-right: 0.75rem;
`;

const CardContent = styled.div`
  color: ${glassTheme.colors.text.primary};
  line-height: 1.8;
  font-size: 1.05rem;
`;

const FormulaBox = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border-left: 4px solid ${glassTheme.colors.accent.blue};
  padding: 1rem 1.5rem;
  margin: 1rem 0;
  border-radius: ${glassTheme.borderRadius.md};
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.primary};
`;

const SelfExplanationSection = styled.div`
  ${glassTheme.glass.light}
  border-radius: ${glassTheme.borderRadius.md};
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 1rem;
`;

const HelperText = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const TextArea = styled.textarea`
  ${glassMixins.input}
  width: 100%;
  min-height: 120px;
  resize: vertical;
`;

const UnderstandingLevel = styled.div`
  margin-top: 1rem;
`;

const LevelButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const LevelButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${props => props.$active ? 'transparent' : glassTheme.colors.border};
  background: ${props => props.$active ? glassTheme.gradients.cyber : 'transparent'};
  color: ${props => props.$active ? glassTheme.colors.text.primary : glassTheme.colors.text.secondary};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${props => props.$active ? 'transparent' : glassTheme.colors.borderHover};
    background: ${props => props.$active ? glassTheme.gradients.cyber : 'rgba(255, 255, 255, 0.08)'};
  }
`;

const SubmitButton = styled.button<{ $disabled: boolean }>`
  ${glassMixins.button}
  width: 100%;
  margin-top: 1rem;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};

  &:hover:not(:disabled) {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled ? 'none' : `${glassTheme.shadow.xl}, ${glassTheme.shadow.glow}`};
    filter: ${props => props.$disabled ? 'none' : 'brightness(1.1)'};
  }
`;

const KeywordsSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${glassTheme.colors.border};
`;

const KeywordTag = styled.span<{ $importance: string }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  margin: 0.25rem;
  border-radius: 20px;
  font-size: 0.85rem;
  background: ${props => {
    switch (props.$importance) {
      case 'essential': return 'rgba(250, 204, 21, 0.15)';
      case 'important': return 'rgba(192, 132, 252, 0.15)';
      default: return 'rgba(156, 163, 175, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$importance) {
      case 'essential': return 'rgba(250, 204, 21, 0.3)';
      case 'important': return 'rgba(192, 132, 252, 0.3)';
      default: return 'rgba(156, 163, 175, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.$importance) {
      case 'essential': return glassTheme.colors.accent.yellow;
      case 'important': return glassTheme.colors.accent.purple;
      default: return glassTheme.colors.text.muted;
    }
  }};
  font-weight: 500;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.secondary};
`;

const ErrorMessage = styled.div`
  ${glassTheme.glass.heavy}
  color: ${glassTheme.colors.status.error};
  padding: 1.5rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
`;

const EmptyContentMessage = styled.div`
  ${glassTheme.glass.heavy}
  color: ${glassTheme.colors.text.secondary};
  padding: 2rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
`;

const PrevCardButton = styled.button`
  padding: 0.75rem 1.5rem;
  align-self: flex-start;
  border: 1px solid ${glassTheme.colors.border};
  background: transparent;
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-size: 0.95rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }
`;

interface Card {
  id: string;
  title: string;
  content: string;
  formula?: string;
  isHtml?: boolean; // true when sourced from learningContent (render as HTML), false/undefined for legacy plain-text cards
}

interface LearningContentEntry {
  _id?: string;
  version: string;
  title: string;
  htmlContent: string;
  isLatest?: boolean;
}

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

interface Keyword {
  term: string;
  definition: string;
  importance: 'essential' | 'important' | 'optional';
}

interface Topic {
  _id: string;
  title: string;
  slug: string;
  description: string;
  category: {
    _id: string;
    name: string;
    slug: string;
    icon: string;
    color: string;
  };
  estimatedTime: number;
  content?: {
    type: string;
    cards?: Card[];
  };
  learningContent?: LearningContentEntry[];
  keywords: Keyword[];
  prerequisites: Array<{
    _id: string;
    title: string;
    slug: string;
  }>;
}

export const TopicDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [explanations, setExplanations] = useState<Record<string, { text: string; level: string }>>({});
  const [progress, setProgress] = useState(0);

  // `topic` is a useState value that is only ever replaced by a brand-new object once,
  // in the fetchTopic effect below (never mutated/recreated on unrelated re-renders),
  // so depending on the full `topic` reference here is safe and mirrors the existing
  // effect further down that already depends on `topic`.
  const displayCards: Card[] = useMemo(() => {
    if (!topic) return [];
    const legacyCards = topic.content?.cards;
    if (Array.isArray(legacyCards) && legacyCards.length > 0) {
      return legacyCards;
    }
    const latest = topic.learningContent?.find(c => c.isLatest) || topic.learningContent?.[0];
    if (latest?.htmlContent) {
      return [{
        id: 'learning-content',
        title: topic.title,
        content: latest.htmlContent,
        isHtml: true
      }];
    }
    return [];
  }, [topic]);

  const {
    trackSelfExplanationSubmit,
    trackTopicComplete
  } = useEventTracking();

  useEffect(() => {
    const fetchTopic = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/topics/${slug}`);
        setTopic(response.data.data.topic);

        // Fetch user progress
        const token = useAuthStore.getState().token;
        if (token) {
          try {
            const progressRes = await axios.get(`/api/topics/${response.data.data.topic._id}/progress`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (progressRes.data.data.progress) {
              const savedExplanations: Record<string, { text: string; level: string }> = {};
              progressRes.data.data.progress.cardsViewed?.forEach((card: any) => {
                savedExplanations[card.cardId] = { text: '', level: 'medium' };
              });
              setExplanations(savedExplanations);
              setProgress(progressRes.data.data.progress.progressPercentage || 0);
            }
          } catch (progressError) {
            console.log('No progress found or error fetching progress');
          }
        }
      } catch (err: any) {
        console.error('Error fetching topic:', err);
        setError(err.response?.data?.message || '加载主题失败');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchTopic();
    }
  }, [slug]);

  // This page has a real Topic id but its display cards are legacy/string
  // presentation keys. Record the content boundary against the accessible Topic
  // rather than inventing a card ObjectId.
  useEffect(() => {
    if (topic && displayCards.length > 0 && OBJECT_ID_PATTERN.test(topic._id)) {
      void recordClientActivity(
        'learning.content_viewed',
        { kind: 'topic', id: topic._id }
      ).catch(() => {
        console.warn('[activity-tracking-failed]', { type: 'learning.content_viewed' });
      });
    }
  }, [topic, displayCards.length]);


  const handleExplanationChange = (cardId: string, text: string) => {
    setExplanations(prev => ({
      ...prev,
      [cardId]: { ...prev[cardId], text }
    }));
  };

  const handleLevelChange = (cardId: string, level: string) => {
    setExplanations(prev => ({
      ...prev,
      [cardId]: { ...prev[cardId], level }
    }));
  };

  const handleSubmitExplanation = async (cardId: string) => {
    const explanation = explanations[cardId];
    if (!explanation || !explanation.text.trim()) {
      toast.error('请先填写你的解释');
      return;
    }

    const token = useAuthStore.getState().token;
    if (!token) {
      toast.error('请先登录');
      return;
    }

    try {
      await axios.post('/api/self-explanations', {
        topic: topic?._id,
        cardId,
        explanation: explanation.text,
        understanding: explanation.level || 'medium',
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Track self-explanation submit
      trackSelfExplanationSubmit({
        topicId: topic!._id,
        cardId,
        explanationLength: explanation.text.length,
        understandingLevel: (explanation.level || 'medium') as 'low' | 'medium' | 'high',
      });

      toast.success('解释已保存！');

      // Update progress
      const newProgress = Math.min(100, progress + (100 / displayCards.length));
      setProgress(newProgress);

      // Update progress on server
      await axios.post(`/api/topics/${topic!._id}/progress`, {
        status: newProgress === 100 ? 'completed' : 'in_progress',
        progressPercentage: newProgress,
        cardsViewed: [{ cardId, viewedAt: new Date() }]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Track topic complete if finished
      if (newProgress === 100) {
        trackTopicComplete({
          topicId: topic!._id,
          topicSlug: topic!.slug,
          cardsCompleted: displayCards.length,
          totalCards: displayCards.length
        });
      }

      // Move to next card
      if (currentCardIndex < displayCards.length - 1) {
        setCurrentCardIndex(prev => prev + 1);
      }
    } catch (err: any) {
      console.error('Error saving explanation:', err);
      toast.error(err.response?.data?.message || '保存失败');
    }
  };

  const currentCard = displayCards[currentCardIndex];
  const [explanationFocused, setExplanationFocused] = useState(false);
  const revision = (topic?.learningContent?.find(c => c.isLatest) || topic?.learningContent?.[0])?._id;
  useForegroundTiming(topic && currentCard ? {context: 'topic', topicId: topic._id,
    contentItemId: currentCard.id, surface: explanationFocused ? 'selfExplanation' : 'reading'} : null,
    !loading && !error && !!currentCard && (topic?.slug === slug || topic?._id === slug),
    currentCard?.isHtml ? revision || null : null);

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>加载中...</LoadingSpinner>
      </PageContainer>
    );
  }

  if (error || !topic) {
    return (
      <PageContainer>
        <ErrorMessage>{error || '主题未找到'}</ErrorMessage>
      </PageContainer>
    );
  }

  const currentCardId = currentCard?.id;
  const explanation = currentCardId ? (explanations[currentCardId] || { text: '', level: 'medium' }) : { text: '', level: 'medium' };

  return (
    <PageContainer>
      <Header $color={topic.category.color}>
        <HeaderContent>
          <Breadcrumb>
            <BreadcrumbLink onClick={() => navigate('/learning')}>学习中心</BreadcrumbLink>
            <span>›</span>
            <BreadcrumbLink onClick={() => navigate('/learning/topics')}>主题列表</BreadcrumbLink>
            <span>›</span>
            <span>{topic.category.name}</span>
          </Breadcrumb>

          <CategoryBadge $color={topic.category.color}>
            {topic.category.icon} {topic.category.name}
          </CategoryBadge>

          <Title>{topic.title}</Title>
          <Description>{topic.description}</Description>

          <MetaInfo>
            <MetaItem>
              <span>⏱</span>
              <span>{topic.estimatedTime} 分钟</span>
            </MetaItem>
            {topic.prerequisites.length > 0 && (
              <MetaItem>
                <span>📚</span>
                <span>需先学习 {topic.prerequisites.length} 个主题</span>
              </MetaItem>
            )}
          </MetaInfo>
        </HeaderContent>
      </Header>

      {displayCards.length === 0 ? (
        <ContentContainer>
          <EmptyContentMessage>此主题内容暂不可用</EmptyContentMessage>
        </ContentContainer>
      ) : (
      <ContentContainer>
        <ProgressBar $progress={progress}>
          <ProgressFill $progress={progress} />
        </ProgressBar>
        <ProgressText>学习进度：{Math.round(progress)}%</ProgressText>

        <CardContainer>
          {displayCards.map((card, index) => (
            <Card
              key={card.id}
              style={{ display: index === currentCardIndex ? 'block' : 'none' }}
            >
              <CardHeader>
                <CardTitle>
                  <CardNumber $index={index + 1}>{index + 1}</CardNumber>
                  {card.title}
                </CardTitle>
              </CardHeader>

              <CardContent>
                {card.isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: card.content }} />
                ) : (
                  card.content
                )}
              </CardContent>

              {card.formula && (
                <FormulaBox>
                  <strong>公式：</strong><br />
                  {card.formula}
                </FormulaBox>
              )}

              <SelfExplanationSection onFocusCapture={() => setExplanationFocused(true)}
                onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setExplanationFocused(false); }}>
                <SectionTitle>✍️ 自我解释</SectionTitle>
                <HelperText>
                  用你自己的话解释一下你对这个概念的理解。这有助于巩固学习效果。
                </HelperText>

                <TextArea
                  placeholder="在这里写下你的理解..."
                  value={explanation.text}
                  onChange={(e) => handleExplanationChange(card.id, e.target.value)}
                />

                <UnderstandingLevel>
                  <HelperText>
                    你的理解程度：
                  </HelperText>
                  <LevelButtons>
                    <LevelButton
                      $active={explanation.level === 'low'}
                      onClick={() => handleLevelChange(card.id, 'low')}
                    >
                      还需学习
                    </LevelButton>
                    <LevelButton
                      $active={explanation.level === 'medium'}
                      onClick={() => handleLevelChange(card.id, 'medium')}
                    >
                      基本理解
                    </LevelButton>
                    <LevelButton
                      $active={explanation.level === 'high'}
                      onClick={() => handleLevelChange(card.id, 'high')}
                    >
                      完全掌握
                    </LevelButton>
                  </LevelButtons>
                </UnderstandingLevel>

                <SubmitButton
                  $disabled={!explanation.text.trim()}
                  onClick={() => handleSubmitExplanation(card.id)}
                >
                  {currentCardIndex < displayCards.length - 1 ? '保存并继续' : '完成学习'}
                </SubmitButton>
              </SelfExplanationSection>

              {index === displayCards.length - 1 && (
                <KeywordsSection>
                  <SectionTitle>🔑 关键概念</SectionTitle>
                  <div>
                    {topic.keywords.map((keyword, idx) => (
                      <KeywordTag key={idx} $importance={keyword.importance}>
                        <strong>{keyword.term}</strong>: {keyword.definition}
                      </KeywordTag>
                    ))}
                  </div>
                </KeywordsSection>
              )}
            </Card>
          ))}

          {currentCardIndex > 0 && (
            <PrevCardButton
              onClick={() => setCurrentCardIndex(prev => prev - 1)}
            >
              ← 上一张卡片
            </PrevCardButton>
          )}
        </CardContainer>
      </ContentContainer>
      )}
    </PageContainer>
  );
};

export default TopicDetail;
